import { defineComponent } from "@vue/composition-api";
import { throttle } from "lodash";
import { CreateElement, VNode } from "vue";
import { MessageThread, MessageThreadsSearchResult } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useSessionStorage, { USER_SETTINGS } from "@/hooks/useSessionStorage";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";
import MessageThreadsView from "./components/MessageThreadsView.vue";
import { MessageSortTypeEnum } from "./types";

export default defineComponent({
  name: "PageMessageThreads",
  components: { MessageThreadsView },
  setup() {
    const { authorities: authUserAuthorities, authUserStrict } = useAuthUser();
    const { getUserSetting, setUserSetting } = useSessionStorage();
    return {
      authUserAuthorities,
      authUserStrict,
      getUserSetting,
      setUserSetting,
    };
  },
  data() {
    return {
      scroll: false,
      loading: false,
      hasMoreThanOneFollowers: false,
      messageThreadsSearchResult: null as MessageThreadsSearchResult | null,
      userMessageSetting: {} as Record<string, any>,
      subscribers: {} as Record<string, Subscription>,
    };
  },
  computed: {
    allowMassMessage(): boolean {
      return checkAuthorities(this.authUserAuthorities, ["updateMyContentPrice"]) && this.hasMoreThanOneFollowers;
    },
    isCreator(): boolean {
      return checkRole(this.authUserStrict?.role ?? "", ["creator"]);
    },
    messageThreadList(): MessageThread[] {
      return this.messageThreadsSearchResult?.result ?? [];
    },
    authUserId(): string {
      return this.authUserStrict.id;
    },
    sortItemsArray() {
      return [
        {
          label: $t("Most recent"),
          sort: "UPDATED_TIME,DESC",
        },
        {
          label: $t("Unread"),
          sort: "UPDATED_TIME,DESC",
          unreadOnly: true,
        },
        {
          label: $t("Oldest unread"),
          sort: "UPDATED_TIME,ASC",
          unreadOnly: true,
        },
        ...(this.isCreator
          ? [
              {
                label: $t("Custom request"),
                sort: "UPDATED_TIME,DESC",
                customRequest: true,
              },
            ]
          : []),
      ];
    },
  },
  methods: {
    async getFollowings() {
      const followings = await providers.FollowingsProvider.find({
        data: {
          userId: this.authUserId,
        },
      });
      this.hasMoreThanOneFollowers = followings.totalRecords > 1;
    },
    async getThreads(page = 0, replace = true) {
      this.scroll = false;

      const sortData = { ...this.sortItemsArray[this.userMessageSetting?.sort || MessageSortTypeEnum.DEFAULT] };
      delete (sortData as Partial<typeof sortData>).label;

      try {
        const response = await providers.MessageThreadsProvider.find({
          data: {
            userId: this.authUserStrict.id,
            page,
            size: 20,
            ...sortData,
          },
        });
        await this.getSubscribers(response.result);
        this.messageThreadsSearchResult = {
          ...response,
          result:
            replace || !this.messageThreadsSearchResult
              ? response.result
              : this.messageThreadsSearchResult.result.concat(response.result),
        };
      } finally {
        this.scroll = this.messageThreadsSearchResult?.hasNextPage ?? false;
      }
    },

    async getSubscribers(threads: MessageThread[]) {
      if (!threads.length || !checkRole(this.authUserStrict.role || "", "creator")) return;
      const consumerIds: string[] = [],
        threadIds: Record<string, string> = {};

      threads.map(i => {
        let ids = i.userIds.filter(i => i !== this.authUserId);
        if (ids.length === 1) {
          consumerIds.push(ids[0]);
          threadIds[ids[0]] = i.id;
        }
      });
      const response = await providers.SubscriptionProvider.search({
        data: {
          consumerIds,
          providerId: this.authUserStrict.id,
          size: consumerIds.length,
          expired: false,
        },
      });
      response.result.map(i => {
        if (threadIds[i.consumerId]) this.subscribers[threadIds[i.consumerId]] = i;
      });
    },
    async getPageData() {
      try {
        this.loading = true;
        await Promise.all([this.getUserMessageSetting(), this.getThreads(), this.getFollowings()]);
      } finally {
        this.loading = false;
      }
    },
    onMessage() {
      this.getThreads();
    },
    getNextPage(cb: Function) {
      const nextPage = (this.messageThreadsSearchResult?.page ?? 0) + 1;
      this.getThreads(nextPage, false).finally(() => cb());
    },
    getUserMessageSetting() {
      this.userMessageSetting = this.getUserSetting(USER_SETTINGS.MESSAGE_SETTINGS);
    },
    saveUserMessageSetting(newSettings: Record<string, any>) {
      this.userMessageSetting = { ...this.userMessageSetting, ...newSettings };
      this.setUserSetting(USER_SETTINGS.MESSAGE_SETTINGS, this.userMessageSetting);
    },
  },
  render(h: CreateElement): VNode {
    return h(MessageThreadsView, {
      props: {
        loading: this.loading,
        authUserId: this.authUserId,
        allowMassMessage: this.allowMassMessage,
        messageThreadList: this.messageThreadList,
        subscribersList: this.subscribers,
        scroll: this.scroll,
        sortItems: this.sortItemsArray.map(item => item.label),
        currentSortItem: this.userMessageSetting?.sort || MessageSortTypeEnum.DEFAULT,
      },
      on: {
        sort: (select: MessageSortTypeEnum) => {
          this.saveUserMessageSetting({ sort: select });
          this.getThreads(0, true);
        },
        massMessage: () => this.$router.push({ name: "massMessage" }),
        next: this.getNextPage,
      },
    });
  },
  created() {
    this.onMessage = throttle(this.onMessage, 3000, { trailing: false });
    this.getPageData();
    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.onMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, this.onMessage);
  },
  beforeDestroy() {
    emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.onMessage);
    emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, this.onMessage);
  },
});
